// frontend/src/modules/projects/components/ProjectMembersSection.tsx
//
// The project roster (migration 080). This is not a decorative list: while a
// project has ZERO members it stays OPEN (anyone may log time against it —
// today's behaviour for every project that existed before 080), and the moment
// it has at least one, the API refuses time entries from anyone off the roster.
// The empty state says exactly that, so nobody adds one name and wonders why
// their colleagues started getting 403s.

import * as React from 'react';
import { toast } from 'sonner';
import { Plus, Users, Trash2, UserPlus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { useApi } from '../../../design-system/useApi';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { projectsApi } from '../api';
import type { ProjectMember, ProjectMemberRole } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

const ROLE_TONE: Record<ProjectMemberRole, StatusTone> = {
  lead: 'info',
  member: 'success',
  reviewer: 'muted',
};

const ROLE_LABEL: Record<ProjectMemberRole, string> = {
  lead: 'Lead',
  member: 'Member',
  reviewer: 'Reviewer',
};

export function ProjectMembersSection({ projectId, employees, canWrite }: {
  projectId: string;
  employees: EmployeeWithUser[];
  canWrite: boolean;
}) {
  const q = useApi<{ members: ProjectMember[] }>(`/api/projects/projects/${projectId}/members`, [projectId]);
  const confirm = useConfirm();
  const [open, setOpen] = React.useState(false);
  const members = q.data?.members ?? [];

  async function remove(m: ProjectMember) {
    const who = m.name ?? 'this employee';
    if (!(await confirm({
      title: `Remove ${who} from this project?`,
      description: members.length === 1
        ? 'This is the last member. With an empty team the project becomes open again — anyone will be able to log time against it.'
        : 'Time they already logged is kept. They will no longer be able to log new time against this project.',
      destructive: true,
      confirmLabel: 'Remove',
    }))) return;
    try {
      await projectsApi.removeMember(projectId, m.employee_id);
      toast.success('Removed from project');
      q.reload();
    } catch (err: any) { toast.error('Remove failed', { description: err.message }); }
  }

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className={VISUAL.dashSection}>Team</h3>
        <div className="flex items-center gap-3">
          <p className="text-[12px] text-muted-foreground">
            {members.length === 0 ? 'Open to everyone' : `${members.length} assigned`}
          </p>
          {canWrite && (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}
              className="rounded-full h-9 px-4 text-[12.5px] font-semibold gap-1.5">
              <Plus className="h-3 w-3" />
              Add member
            </Button>
          )}
        </div>
      </div>
      <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        {q.loading ? (
          <div className="py-8 px-6 text-[13px] text-muted-foreground">Loading…</div>
        ) : q.error ? (
          <ErrorState title="Couldn't load the team" description={q.error} onRetry={q.reload} />
        ) : members.length === 0 ? (
          <div className="py-10 px-6 text-center">
            <Users className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
            <p className="text-[14px] font-semibold text-foreground">No one assigned yet</p>
            <p className="text-[13px] text-muted-foreground mt-1 max-w-md mx-auto">
              While this list is empty the project is open — anyone can log time against it.
              Assign the first person and only the team below will be able to.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {members.map((m) => (
              <li key={m.id} className="flex items-center gap-3 px-5 py-3.5">
                <div className="h-9 w-9 rounded-2xl bg-muted/50 dark:bg-white/[0.06] ring-1 ring-border/40 flex items-center justify-center shrink-0">
                  <span className="text-[12px] font-semibold text-muted-foreground">{initials(m.name)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-foreground truncate">
                    {m.name ?? m.employee_id.slice(0, 8)}
                    {m.is_active === false && (
                      <span className="ml-2 text-[11.5px] font-normal text-muted-foreground">(inactive)</span>
                    )}
                  </p>
                  <p className="text-[12px] text-muted-foreground mt-0.5 truncate">
                    {m.designation || m.email || '—'}
                  </p>
                </div>
                <span className={statusPill(ROLE_TONE[m.role] ?? 'muted')}>
                  <span className={statusDot(ROLE_TONE[m.role] ?? 'muted')} />
                  {ROLE_LABEL[m.role] ?? m.role}
                </span>
                {canWrite && (
                  <button type="button" onClick={() => remove(m)}
                    className="text-muted-foreground hover:text-rose-500 dark:hover:text-rose-400 transition-colors p-1"
                    aria-label={`Remove ${m.name ?? 'member'} from project`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <AddMemberDialog
        open={open}
        onOpenChange={setOpen}
        projectId={projectId}
        employees={employees}
        existing={members}
        onAdded={() => { setOpen(false); q.reload(); }}
      />
    </section>
  );
}

function AddMemberDialog({ open, onOpenChange, projectId, employees, existing, onAdded }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
  employees: EmployeeWithUser[];
  existing: ProjectMember[];
  onAdded: () => void;
}) {
  const [employeeId, setEmployeeId] = React.useState('');
  const [role, setRole] = React.useState<ProjectMemberRole>('member');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => { if (open) { setEmployeeId(''); setRole('member'); } }, [open]);

  // Already-assigned people are dropped from the picker: re-adding is a role
  // change on the server, but offering it here reads like a duplicate.
  const assigned = new Set(existing.map((m) => m.employee_id));
  const available = employees.filter((e) => !assigned.has(e.id));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employeeId) { toast.error('Pick an employee'); return; }
    setBusy(true);
    try {
      await projectsApi.addMember(projectId, { employee_id: employeeId, role });
      toast.success('Added to project');
      onAdded();
    } catch (err: any) {
      toast.error('Add failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add someone to this project</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Employee<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger><SelectValue placeholder={available.length ? 'Choose an employee' : 'No one left to add'} /></SelectTrigger>
              <SelectContent>
                {available.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Role on this project</Label>
            <Select value={role} onValueChange={(v) => setRole(v as ProjectMemberRole)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lead">Lead — accountable for the project</SelectItem>
                <SelectItem value="member">Member — does the work</SelectItem>
                <SelectItem value="reviewer">Reviewer — reads and reviews</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {existing.length === 0 && (
            <p className="text-[12px] text-amber-700 dark:text-amber-300 bg-amber-500/10 dark:bg-amber-400/10 ring-1 ring-amber-500/25 dark:ring-amber-400/25 rounded-2xl px-3.5 py-2.5 flex gap-2">
              <UserPlus className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                This is the first person on the project. From now on only assigned members
                will be able to log time against it.
              </span>
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !employeeId}>{busy ? 'Adding…' : 'Add to project'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function initials(name?: string | null): string {
  if (!name) return '–';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '–';
}
